import { zodResolver } from "@hookform/resolvers/zod";
import { X } from "@tamagui/lucide-icons";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import {
  Adapt,
  Button,
  Dialog,
  H1,
  Input,
  Sheet,
  XStack,
  YStack,
} from "@/components";

const manualMedicationSchema = z.object({
  name: z.string().trim().min(1, "Required"),
  strength: z.string().trim().min(1, "Required"),
  directions: z.string().trim().min(1, "Required"),
});

export type ManualMedication = z.infer<typeof manualMedicationSchema>;

type Props = {
  open: boolean;
  onClose: () => void;
  onAdd: (med: ManualMedication) => void;
};

export const AddMedicationDialog = ({ open, onClose, onAdd }: Props) => {
  const { control, handleSubmit, reset } = useForm<ManualMedication>({
    resolver: zodResolver(manualMedicationSchema),
    defaultValues: { name: "", strength: "", directions: "" },
  });

  useEffect(() => {
    if (!open) reset({ name: "", strength: "", directions: "" });
  }, [open, reset]);

  const handleAdd = handleSubmit((values) => {
    onAdd({
      name: values.name.trim(),
      strength: values.strength.trim(),
      directions: values.directions.trim(),
    });
    onClose();
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen: boolean) => !isOpen && onClose()}
    >
      <Adapt when="maxLg">
        <Sheet
          modal
          open={open}
          onOpenChange={(isOpen: boolean) => !isOpen && onClose()}
          disableDrag
        >
          <Sheet.Frame padding="$300" gap="$200">
            <Adapt.Contents />
          </Sheet.Frame>
          <Sheet.Overlay
            backgroundColor="$fillOverlay"
            backdropFilter="blur(8px)"
          />
        </Sheet>
      </Adapt>
      <Dialog.Portal>
        <Dialog.Overlay
          backgroundColor="$fillOverlay"
          backdropFilter="blur(8px)"
        />
        <Dialog.Content
          borderRadius="$150"
          padding="$300"
          minWidth={400}
          maxWidth={500}
          gap="$200"
        >
          <XStack justifyContent="space-between" alignItems="center">
            <Dialog.Title asChild>
              <H1 size="$lg" $lg={{ size: "$xl" }}>
                Add a medication
              </H1>
            </Dialog.Title>
            <Dialog.Close asChild displayWhenAdapted>
              <Button.Wrapper aria-label="Close" onPress={onClose}>
                <X size={24} />
              </Button.Wrapper>
            </Dialog.Close>
          </XStack>

          <YStack gap="$150">
            <Controller
              control={control}
              name="name"
              render={({ field, fieldState }) => (
                <Input.Controlled
                  label="Medication name"
                  placeholder="e.g. Lisinopril"
                  {...field}
                  value={field.value ?? ""}
                  errorText={fieldState.error?.message}
                />
              )}
            />
            <Controller
              control={control}
              name="strength"
              render={({ field, fieldState }) => (
                <Input.Controlled
                  label="Strength"
                  placeholder="e.g. 10 mg"
                  {...field}
                  value={field.value ?? ""}
                  errorText={fieldState.error?.message}
                />
              )}
            />
            <Controller
              control={control}
              name="directions"
              render={({ field, fieldState }) => (
                <Input.Controlled
                  label="Frequency"
                  placeholder="e.g. Once daily"
                  {...field}
                  value={field.value ?? ""}
                  errorText={fieldState.error?.message}
                />
              )}
            />
          </YStack>

          <XStack justifyContent="flex-end" gap="$100">
            <Button
              mode="primary"
              onPress={handleAdd}
              dataTrackingId="add-medication-submit-button"
            >
              Add
            </Button>
          </XStack>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
};
